import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { accountService } from '../Api/Services/accountService';
import { InStoreUserRegister, SimplifiedUser, UserLogin, UserRegister } from '../Models/User';
import { AuthContextType } from '../Types/AuthContextType';
import { RegisterInStoreResponse } from '../Types/RegisterInStoreResponseType';
import { toPublicPath } from '../Utils/publicRoutes';
import { clearUserSession, getUserSession, persistUserSession } from '../Utils/AuthUtils';
import { toast } from 'react-toastify';

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<SimplifiedUser | null>(null);
  const [loading, setLoading] = useState(true);

  const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

  const isAdmin = user?.role === 'Admin' || user?.role === 'TenantOwner';
  const isOperator = user?.role === 'Operator';
  const isGoogleUser = user?.isGoogleUser === true;

  useEffect(() => {
    const storedUser = getUserSession();

    if (storedUser) {
      setUser(storedUser);
    }
    setLoading(false);
  }, []);

  const login = async (data: UserLogin): Promise<SimplifiedUser | null> => {
    const { user, message } = await accountService.login(data);

    if (user) {
      if (!user.requiresContactInfoUpdate) {
        toast.success("Login realizado com sucesso!");
      }
      setUser(user);
      persistUserSession(user);
      return user;
    }

    if (message) {
      toast.error(message);
    }
    return null;
  };

  const googleLogin = async (idToken: string, accessToken?: string): Promise<SimplifiedUser | null> => {
    const { user, message } = await accountService.googleLogin(idToken, accessToken);

    if (user) {
      if (!user.requiresContactInfoUpdate) {
        toast.success("Login realizado com sucesso!");
      }
      setUser(user);
      persistUserSession(user);
      return user;
    }

    if (message) {
      toast.error(message);
    }
    return null;
  };

  const googlePopupLogin = async (mode: 'signin' | 'signup' = 'signin'): Promise<SimplifiedUser | null> => {
    const authApiBaseUrl = process.env.REACT_APP_GOOGLE_AUTH_API_BASE_URL
      || (process.env.NODE_ENV === 'production' ? 'https://api.essaecommerce.com/api' : 'http://localhost:5079/api');

    let popupOrigin: string;
    try {
      popupOrigin = new URL(authApiBaseUrl).origin;
    } catch {
      toast.error("Configuração inválida de autenticação com Google.");
      return null;
    }

    const requestId = crypto.randomUUID();
    const popupUrl = `${authApiBaseUrl}/account/google/popup?origin=${encodeURIComponent(window.location.origin)}&mode=${encodeURIComponent(mode)}&requestId=${encodeURIComponent(requestId)}`;
    const popupWidth = 520;
    const popupHeight = 720;
    const screenLeft = typeof window.screenLeft === 'number' ? window.screenLeft : window.screenX;
    const screenTop = typeof window.screenTop === 'number' ? window.screenTop : window.screenY;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || window.screen.height;
    const left = Math.max(0, Math.round(screenLeft + (viewportWidth - popupWidth) / 2));
    const top = Math.max(0, Math.round(screenTop + (viewportHeight - popupHeight) / 2));

    const popup = window.open(
      popupUrl,
      'essa-google-auth',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      toast.error("Não foi possível abrir o login com Google. Verifique se o bloqueador de pop-up está desativado.");
      return null;
    }

    return await new Promise<SimplifiedUser | null>((resolve) => {
      let settled = false;
      let credentialReceived = false;
      let timeoutWatcher: ReturnType<typeof setTimeout> | null = null;
      let closedWatcher: ReturnType<typeof setInterval> | null = null;

      const cleanup = () => {
        window.removeEventListener('message', handleMessage);
        if (timeoutWatcher) {
          clearTimeout(timeoutWatcher);
          timeoutWatcher = null;
        }
        if (closedWatcher) {
          clearInterval(closedWatcher);
          closedWatcher = null;
        }
      };

      const finish = (result: SimplifiedUser | null) => {
        if (settled) {
          return;
        }

        settled = true;
        cleanup();
        resolve(result);
      };

      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== popupOrigin) {
          return;
        }

        const data = event.data;
        if (!data || data.source !== 'essa-google-auth' || data.requestId !== requestId) {
          return;
        }

        if (data.error) {
          toast.error(data.error as string);
          finish(null);
          return;
        }

        if (!data.credential) {
          finish(null);
          return;
        }

        credentialReceived = true;
        const authenticatedUser = await googleLogin(data.credential as string, data.accessToken as string | undefined);
        finish(authenticatedUser);
      };

      window.addEventListener('message', handleMessage);

      timeoutWatcher = setTimeout(() => {
        finish(null);
      }, 120000);

      closedWatcher = setInterval(() => {
        if (popup.closed && !credentialReceived) {
          finish(null);
        }
      }, 500);
    });
  };

  const register = async (data: UserRegister): Promise<boolean> => {
    try {
      const message = await accountService.register(data);
      toast.success(message);
      return true;
    } catch (err: any) {
      const backendMsg = err.response?.data?.message || err.response?.data || "Algo deu errado. Tente novamente.";
      toast.error(backendMsg);
      return false;
    }
  };

  const registerInStore = async (data: InStoreUserRegister): Promise<RegisterInStoreResponse | null> => {
    try {
      const result = await accountService.registerInStore(data);
      toast.success(result.message);
      return result;
    } catch (err: any) {
      const backendMsg = err.response?.data?.message || err.response?.data || "Algo deu errado. Tente novamente.";
      toast.error(backendMsg);
      return null;
    }
  };

  const updateUser = useCallback((updates: Partial<SimplifiedUser>) => {
    setUser(prev => {
      if (!prev) {
        return prev;
      }
      const updated = { ...prev, ...updates };
      persistUserSession(updated);
      return updated;
    });
  }, []);

  const logout = useCallback(async (options?: { redirect?: boolean; silent?: boolean; reason?: 'manual' | 'expired' | 'idle' }) => {
    const { redirect = false, silent = false, reason = 'manual' } = options || {};

    try {
      await accountService.logout();
    } catch {
      // If the API is unavailable we still clear the local session below.
    }

    clearUserSession();
    setUser(null);

    if (!silent) {
      const messages: Record<'manual' | 'expired' | 'idle', string> = {
        manual: "Você saiu da sua conta.",
        expired: "Sua sessão expirou. Faça login novamente.",
        idle: "Sessão encerrada por inatividade. Faça login novamente."
      };
      toast.info(messages[reason]);
    }

    if (redirect) {
      window.location.href = toPublicPath("/login");
    }
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        logout({ redirect: true, silent: false, reason: 'idle' });
      }, SESSION_TIMEOUT_MS);
    };

    const events = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, resetTimer));

    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [user, logout, SESSION_TIMEOUT_MS]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        isOperator,
        isGoogleUser,
        loading,
        register,
        registerInStore,
        updateUser,
        login,
        googleLogin,
        googlePopupLogin,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
